/**
 * Sensor expression composer / decomposer.
 *
 * Most fleet installations need one of three transforms on the raw value:
 *   - Invert (boolean): the device wires ignition active-low and the truth
 *     table needs flipping.
 *   - Scale: voltage divider, analog fuel sensor, etc.
 *   - Offset: zero-point calibration.
 *
 * GPSgate exposes these as three labeled fields; Trackizy historically only
 * exposed the raw Traccar expression. These helpers compose/decompose the
 * common shapes so the UI can offer the simple fields while keeping the raw
 * expression authoritative for the cases that don't fit (custom formulas).
 */

function escapeRegex(s) {
  return String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build a Traccar expression from an attribute name + transform values.
 * @param {string} attribute
 * @param {{ invert?: boolean, scale?: number, offset?: number, type?: string }} transform
 * @returns {string}
 */
export function composeExpression(attribute, transform = {}) {
  const attr = String(attribute || '').trim();
  if (!attr) return '';
  const type = transform.type || 'Number';
  if (type === 'Boolean') return transform.invert ? `!${attr}` : attr;
  const scale = Number(transform.scale ?? 1);
  const offset = Number(transform.offset ?? 0);
  const hasScale = Number.isFinite(scale) && scale !== 1;
  const hasOffset = Number.isFinite(offset) && offset !== 0;
  if (!hasScale && !hasOffset) return attr;
  if (hasScale && hasOffset) {
    const sign = offset >= 0 ? '+' : '-';
    return `${attr} * ${scale} ${sign} ${Math.abs(offset)}`;
  }
  if (hasScale) return `${attr} * ${scale}`;
  const sign = offset >= 0 ? '+' : '-';
  return `${attr} ${sign} ${Math.abs(offset)}`;
}

/**
 * Inverse of composeExpression. Returns the transform fields for the cases
 * we can recognise, and { custom: true } otherwise so the UI can show
 * "Custom expression" instead of pretending to be in quick-transform mode.
 *
 * @param {string} expression
 * @param {string} attribute
 * @param {string} type  'Number' | 'Boolean' | 'String'
 * @returns {{ invert: boolean, scale: number, offset: number, custom: boolean }}
 */
export function decomposeExpression(expression, attribute, type = 'Number') {
  const expr = String(expression || '').trim();
  const attr = String(attribute || '').trim();
  const ZERO = { invert: false, scale: 1, offset: 0, custom: false };
  if (!expr || !attr) return { ...ZERO, custom: !!expr && expr !== attr };

  if (type === 'Boolean') {
    if (expr === attr) return ZERO;
    if (expr === `!${attr}`) return { ...ZERO, invert: true };
    return { ...ZERO, custom: true };
  }

  if (type === 'String') {
    return expr === attr ? ZERO : { ...ZERO, custom: true };
  }

  // Numeric — try the four common shapes in order of specificity.
  const A = escapeRegex(attr);
  // attr * S + O   /  attr * S - O
  const m1 = expr.match(new RegExp(`^${A}\\s*\\*\\s*(-?\\d+(?:\\.\\d+)?)\\s*([+\\-])\\s*(\\d+(?:\\.\\d+)?)$`));
  if (m1) {
    return { invert: false, scale: Number(m1[1]), offset: (m1[2] === '-' ? -1 : 1) * Number(m1[3]), custom: false };
  }
  // attr * S
  const m2 = expr.match(new RegExp(`^${A}\\s*\\*\\s*(-?\\d+(?:\\.\\d+)?)$`));
  if (m2) {
    return { invert: false, scale: Number(m2[1]), offset: 0, custom: false };
  }
  // attr + O   /  attr - O
  const m3 = expr.match(new RegExp(`^${A}\\s*([+\\-])\\s*(\\d+(?:\\.\\d+)?)$`));
  if (m3) {
    return { invert: false, scale: 1, offset: (m3[1] === '-' ? -1 : 1) * Number(m3[2]), custom: false };
  }
  // attr (identity)
  if (expr === attr) return ZERO;
  return { ...ZERO, custom: true };
}
